import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { AenApplicationsListPageLayout } from "./aen-applications-list-page-layout";
import { AenApplicationType } from "models/enumerations/aen-applications/aen-application-types";
import { Badge } from "components/badges/badge/badge";
import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { CollectionUtils } from "utilities/collection-utils";
import { ContextMenu } from "components/context-menu/context-menu/context-menu";
import { ContextMenuButton } from "components/context-menu/context-menu-button/context-menu-button";
import { DataTable } from "components/tables/data-table/data-table";
import { DateUtils } from "utilities/date-utils";
import { EmptyText } from "components/empty-text/empty-text";
import { EnumUtils } from "utilities/enumerations/enum-utils";
import { FormSelect } from "components/form/form-select/form-select";
import {
    InstructorApplicationStatus,
    InstructorApplicationStatusBadgeStyleMap,
    InstructorApplicationStatusDisplayNames,
} from "models/enumerations/aen-applications/instructor-application-status";
import {
    ITEMS_PER_PAGE,
    useAenApplications,
} from "utilities/hooks/models/aen-applications/use-aen-applications";
import { Pager } from "components/pager/pager";
import { Paragraph, ParagraphStyle } from "components/typography/paragraph/paragraph";
import { RouteUtils } from "utilities/route-utils";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { sitemap } from "sitemap";
import { t } from "utilities/localization/t";
import { useNavigate } from "utilities/hooks/navigation/use-navigate";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import React, { JSX, useEffect } from "react";
import { useApplicationSearch } from "utilities/contexts/search/aen-applications/application-search-context";
import "./../aen-applications.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AenApplicationsInstructorProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const CSS_CLASS_NAME = "aen-applications";
const DEBOUNCE_TIME = 750;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AenApplicationsInstructor: React.FC<AenApplicationsInstructorProps> = validatePageAccess(
    AccessControlKeys.AdminAenInstructorApplications
)((): JSX.Element => {
    const navigate = useNavigate();

    const { initialCriteria: criteria, setCriteria } = useApplicationSearch();

    const {
        assignedToId,
        assignedToOptions,
        currentPage,
        getAssignedToNameById,
        handleAssignedToChange,
        handleSearchTextInputChange,
        handleStatusChange,
        isLoadingInstructorApplications,
        onClearFilterClick,
        onPageClick,
        instructorApplications,
        searchText,
        handleSearchTriggered,
        status,
        totalInstructorApplications
    } = useAenApplications({ 
            applicationType: AenApplicationType.Instructor,
            initialCriteria: criteria,
     });

    useEffect(() => {
        const instructorApplicationStatus = status == null ? undefined : status as InstructorApplicationStatus;
        setCriteria((prev) => ({ ...prev, 
            searchText: searchText,
            status: instructorApplicationStatus,
            assignedToId: assignedToId,
            currentPage: currentPage }));
    }, [searchText, currentPage, status, assignedToId]);

    return (
        <AenApplicationsListPageLayout
            footer={
                <Pager
                    currentPage={currentPage}
                    onPageClick={onPageClick}
                    totalPages={totalInstructorApplications / ITEMS_PER_PAGE}
                    itemsPerPage={ITEMS_PER_PAGE}
                    totalItems={totalInstructorApplications}></Pager>
            }
            filtersElement={
                <div className={`${CSS_CLASS_NAME}__sidebar`}>
                    <Paragraph style={ParagraphStyle.Label}>{t("filterResults")}</Paragraph>
                    <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                        <SearchTextInput
                            debounce={DEBOUNCE_TIME}
                            disabled={isLoadingInstructorApplications}
                            onSearchTextInputChange={handleSearchTextInputChange}
                            onSearchTriggered={handleSearchTriggered}
                            id={"aenApplicationsSearch"}
                            placeholder={t("searchByEmailNameOrId")}
                            searchTextInputValue={searchText}
                        />
                    </div>
                    <FormSelect
                        disabled={isLoadingInstructorApplications}
                        ariaLabelledBy={t("filterByRole")}
                        formFieldName={t("filterByRole")}
                        id="applicationsFilterByStatus"
                        label={t("filterByStatus")}
                        onChange={handleStatusChange}
                        options={[
                            { text: "", value: "" },
                            ...EnumUtils.numericEnumToSelectOptions(
                                InstructorApplicationStatus,
                                InstructorApplicationStatusDisplayNames
                            ),
                        ]}
                        value={status?.toString()}
                    />
                    <FormSelect
                        disabled={isLoadingInstructorApplications}
                        ariaLabelledBy={t("filterByAssignedTo")}
                        formFieldName={t("filterByAssignedTo")}
                        id="applicationsFilterByAssignedTo"
                        label={t("filterByAssignedTo")}
                        onChange={handleAssignedToChange}
                        options={[{ text: "", value: "" }, ...assignedToOptions]}
                        value={assignedToId?.toString()}
                    />
                    <Button
                        disabled={isLoadingInstructorApplications}
                        onClick={onClearFilterClick}
                        size={ButtonSize.Small}
                        style={ButtonStyle.Quaternary}
                        text={t("clearAllFilters")}
                    />
                </div>
            }>
            {CollectionUtils.hasValues(instructorApplications) ? (
                <>
                    <DataTable cssClassName={`${CSS_CLASS_NAME}__table`}>
                        <thead>
                            <tr>
                                <th className="id">{t("id")}</th>
                                <th className="instructor">{t("instructor")}</th>
                                <th className="contactEmail">{t("contactEmail")}</th>
                                <th className="assignedTo">{t("assignedTo")}</th>
                                <th className="date">{t("date")}</th>
                                <th className="status">{t("status")}</th>
                                <th className="action">
                                    <span className="sr-only">{t("action")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {instructorApplications.map((application, idx) => (
                                <tr key={application.id}>
                                    <td className="id">{application.id}</td>
                                    <td className="instructor">
                                        {application.firstName} {application.lastName}
                                    </td>
                                    <td className="contactEmail">{application.email}</td>
                                    <td className="assignedTo">
                                        {getAssignedToNameById(application.assignedToId!)}
                                    </td>
                                    <td className="dateCreated">
                                        {DateUtils.formatDateTime(
                                            true,
                                            application?.createdOn?.toString(),
                                            true
                                        )}
                                    </td>
                                    <td className="status">
                                        <Badge
                                            style={
                                                InstructorApplicationStatusBadgeStyleMap[
                                                    application.status as InstructorApplicationStatus
                                                ]
                                            }
                                            text={t(
                                                InstructorApplicationStatusDisplayNames[
                                                    application.status as InstructorApplicationStatus
                                                ]
                                            )}
                                        />
                                    </td>
                                    <td className="action">
                                        <ContextMenu>
                                            <ContextMenuButton
                                                displayName={t("goToApplication")}
                                                onClick={() =>
                                                    navigate(
                                                        RouteUtils.replacePathParams(
                                                            sitemap.admin.aenApplications
                                                                .instructors.detail,
                                                            {
                                                                id: application.id,
                                                            }
                                                        )
                                                    )
                                                }
                                            />
                                        </ContextMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                </>
            ) : (
                <EmptyText table>{t("noContentResultsFound")}</EmptyText>
            )}
        </AenApplicationsListPageLayout>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AenApplicationsInstructor };

// #endregion Exports
